// HiringDashboard — the console's one Dashboard (owner, 29 Oct 2026).
//
// The admin Overview merged into it: the headline figures sit on top, for
// the period chosen, then the candidate journey with the "act now" queues
// beside it. What used to stretch the page (the reminders, the round
// announcement, the download, the reminder report, the candidate questions
// and the offer follow-ups) opens in a window from a row of buttons, so the
// page reads at a glance. Alerts and hints moved to the bell in the top bar
// (components/layout/NoticeBell.jsx); its links open the window they name
// here (?open=questions | offers | orphans). Data comes from GET
// /api/dashboard, tenant-scoped to the signed-in school. EquaOS design system.

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { inShell } from '../../shell';
import { Card, CardHeader, Button, Modal, StatusPill, Icon } from '../../ds';
import OfferFollowUps from './OfferFollowUps';
import ReminderReport from './ReminderReport';
import ApplicationsExport from './ApplicationsExport';
import RoundAnnouncement from './RoundAnnouncement';
import { NudgeWindow, ActNowSettings, AiSuggestions, SetupNotReady } from './ActNowTools';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: 'all', label: 'All' },
  { key: 'custom', label: 'Custom' },
];

/* The funnel's steps narrow evenly from the top step to FUNNEL_NARROWEST,
   whatever the numbers, so it always reads as a funnel; each step's number
   is written in it. Darker teal further down (the design system's teal ramp). */
const FUNNEL_NARROWEST = 38; // % of the widest step
const FUNNEL_SHADES = ['var(--teal-400)', 'var(--teal-500)', 'var(--teal-600)', 'var(--teal-700)', 'var(--teal-800)', 'var(--teal-900)'];

const pctText = (n) => (n === null || n === undefined ? '—' : `${n}%`);

export default function HiringDashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [period, setPeriod] = useState('7d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // Which window is open: remind | round | export | report | questions | offers.
  const [win, setWin] = useState(null);
  const [remind, setRemind] = useState({ phase: 'idle' }); // idle | loading | confirm | sending | done | error
  // Candidate questions the Application Helper passed to the team (HIR-CN5).
  const [questions, setQuestions] = useState({ items: [], loading: false, msg: '' });
  const [remindedRuns, setRemindedRuns] = useState(0); // bumps the reminder report after a send
  // One role's own journey through its pipeline (30 Oct 2026): '' = all roles.
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState('');
  const [roleJourney, setRoleJourney] = useState(null);
  useEffect(() => {
    api.get('/jobs', { params: { limit: 100 } })
      .then((r) => setRoles(r.data?.data?.jobs || r.data?.data || r.data?.jobs || []))
      .catch(() => setRoles([]));
  }, []);
  useEffect(() => {
    setRoleJourney(null);
    if (!roleId) return;
    api.get('/dashboard/role-journey', { params: { jobId: roleId } })
      .then((r) => setRoleJourney(r.data?.data || null))
      .catch(() => setRoleJourney({ error: true, steps: [] }));
  }, [roleId]);

  // target: 'unpaid' | 'orphans'. The unpaid reminder states the online
  // assessment date: `assessmentDate` re-runs the preview with the date typed.
  const remindPreview = (target, assessmentDate) => {
    setWin('remind');
    setRemind((s) => ({ ...(s.phase === 'confirm' ? s : {}), phase: 'loading', target }));
    Promise.all([
      api.post(`/dashboard/remind-${target}?dry=1`, assessmentDate !== undefined ? { assessmentDate } : {}),
      api.get('/school-settings/message-templates').catch(() => ({ data: { data: { available: { email: [], whatsapp: [] } } } })),
    ]).then(([r, cat]) => {
      const avail = cat.data?.data?.available || { email: [], whatsapp: [] };
      const find = (list, key) => (list || []).find((t) => t.key === key) || null;
      const tpl = r.data.templates || {};
      setRemind({
        phase: 'confirm', target,
        count: r.data.willRemind, skipped: r.data.skipped,
        plan: r.data.plan || [],
        templates: tpl,
        emailTpl: find(avail.email, tpl.email),
        waTpl: find(avail.whatsapp, tpl.wa),
        // The unpaid reminder: the words rendered by the server, and the date.
        preview: r.data.preview || null,
        missing: r.data.missing || [],
        assessmentDate: assessmentDate !== undefined ? assessmentDate : (r.data.round?.assessmentDate || ''),
        // What the preview shows; a date typed since must be previewed first.
        previewedDate: assessmentDate !== undefined ? assessmentDate : (r.data.round?.assessmentDate || ''),
      });
    }).catch(() => setRemind({ phase: 'error', target, msg: 'Could not prepare the reminders.' }));
  };
  const remindSend = () => {
    const target = remind.target;
    setRemind((s) => ({ ...s, phase: 'sending' }));
    api.post(`/dashboard/remind-${target}`, target === 'unpaid' ? { assessmentDate: remind.assessmentDate || '' } : {})
      .then((r) => { setRemind({ phase: 'done', target, sent: r.data.sent, failed: r.data.failed }); setRemindedRuns((n) => n + 1); })
      .catch((e) => setRemind({ phase: 'error', target, msg: e?.response?.data?.message || 'Sending failed — try again or tell your developer.' }));
  };

  // Rule one "paid, no application" entry out by hand (a test payment, or a
  // stale entry for someone who in fact applied). Removes it from this preview
  // and from the count; the send then goes only to whoever is left.
  const dismissOrphan = (ref) => {
    setRemind((s) => ({ ...s, dismissing: ref }));
    api.post('/dashboard/orphans/dismiss', { ref })
      .then(() => {
        setRemind((s) => {
          const plan = (s.plan || []).filter((p) => p.ref !== ref);
          return plan.length
            ? { ...s, plan, count: plan.length, dismissing: null }
            : { phase: 'idle' };
        });
        load();
      })
      .catch(() => setRemind((s) => ({ ...s, dismissing: null, msg: 'Could not dismiss that entry — reload and try again.' })));
  };

  const loadQuestions = () => {
    setWin('questions');
    setQuestions((q) => ({ ...q, loading: true, msg: '' }));
    api.get('/dashboard/candidate-questions')
      .then((r) => setQuestions({ loading: false, items: r.data.items || [], msg: '' }))
      .catch(() => setQuestions((q) => ({ ...q, loading: false, msg: 'Could not load the questions.' })));
  };
  const markAnswered = (id) => {
    api.post(`/dashboard/candidate-questions/${id}/answered`)
      .then(() => { setQuestions((q) => ({ ...q, items: q.items.filter((x) => x.id !== id) })); load(); })
      .catch(() => setQuestions((q) => ({ ...q, msg: 'Could not mark it answered — reload and try again.' })));
  };

  const school = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').companyName || ''; } catch { return ''; } })();

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams({ period });
    if (period === 'custom') { if (from) q.set('from', from); if (to) q.set('to', to); }
    return api.get(`/dashboard?${q.toString()}`)
      .then((r) => { setData(r.data); setErr(''); })
      .catch(() => setErr('Could not load the dashboard. If this persists, tell your developer.'))
      .finally(() => setLoading(false));
  }, [period, from, to]);

  useEffect(() => { if (period !== 'custom' || (from && to)) load(); }, [load, period, from, to]);

  // A notice from the bell names the window to open.
  useEffect(() => {
    const open = params.get('open');
    if (!open) return;
    if (open === 'questions') loadQuestions();
    else if (open === 'offers') setWin('offers');
    else if (open === 'orphans') remindPreview('orphans');
    // From a communication's page in Messages: the same windows as the buttons.
    else if (open === 'unpaid') remindPreview('unpaid');
    else if (open === 'round') setWin('round');
    params.delete('open');
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const closeWin = () => { setWin(null); if (remind.phase !== 'sending') setRemind({ phase: 'idle' }); };

  const goList = (qs) => navigate(`/talent-pool${qs ? `?${qs}` : ''}`);
  // Which filtered list each "act now" queue opens.
  const ACT_QUERY = { toScreen: 'awaiting=1', f2f: 'reached=f2f', offers: 'reached=offered', unpaid: 'payment=unpaid', aging: 'awaiting=1' };
  const actOn = (a) => {
    if (a.key === 'questions') return loadQuestions();
    if (a.key === 'offerFollowUps') return setWin('offers');
    if (a.key === 'orphans') return remindPreview('orphans');
    // Invited, not taken their round: the nudge window (30 Oct 2026).
    if (a.key === 'notTakenTest') return setWin('nudge');
    // Roles with a test or interview brief not ready (@story HIR-HR21).
    if (a.key === 'setupNotReady') return setWin('setup');
    return goList(ACT_QUERY[a.key] || '');
  };

  const unpaid = data?.actNow.find((a) => a.key === 'unpaid')?.count || 0;
  const orphans = data?.paidNoApplication || 0;
  const k = data?.kpis;

  return (
    <div style={{ padding: '24px', display: 'grid', gap: '20px' }}>
      {/* Title and the period */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 'var(--fs-display-md)', fontWeight: 600 }}>Dashboard</h1>
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>{school ? `${school} · ` : ''}{PERIODS.find((p) => p.key === period)?.label}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }} role="group" aria-label="Period">
          {PERIODS.map((p) => (
            <Button key={p.key} size="sm" variant={period === p.key ? 'primary' : 'secondary'} onClick={() => setPeriod(p.key)} aria-pressed={period === p.key}
              icon={p.key === 'custom' ? <Icon name="calendar" /> : null}>{p.label}</Button>
          ))}
          <Button size="sm" variant="ghost" onClick={load} aria-label="Refresh" icon={<Icon name="arrow-clockwise" spin={loading} />} />
          {/* Sign out on the Dashboard too, as the frame's Home has it (issue 24). In the
              ERP the ERP's own menu signs out. */}
          {!inShell && (
            <Button size="sm" variant="ghost" icon={<Icon name="sign-out" />} onClick={() => window.dispatchEvent(new Event('hiring:sign-out'))}>Sign out</Button>
          )}
        </div>
      </div>
      {period === 'custom' && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>
          <label>From <input type="date" className="eq-native-select" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label>To <input type="date" className="eq-native-select" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        </div>
      )}

      {err && <Card><span style={{ color: 'var(--status-warning-text)' }}>{err}</span></Card>}
      {loading && !data && <Card><span style={{ color: 'var(--text-muted)' }}>Loading the dashboard…</span></Card>}

      {data && (
        <>
          {/* The headline figures: this school, this period */}
          <div data-testid="tiles" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            <Tile label="Applicants" value={data.tiles.applicants} sub="in this period" onClick={() => goList('')} />
            <Tile label="Strong (80%+)" value={data.tiles.strong} sub="ready to shortlist" onClick={() => goList('minScore=80')} />
            <Tile label="Fee paid" value={`${data.tiles.paidPct}%`} sub={`${data.tiles.paid} of ${data.tiles.applicants}`} onClick={() => goList('payment=paid')} />
            {k && <Tile label="Active jobs" value={k.activeJobs} sub={`of ${k.totalJobs}`} onClick={() => navigate('/jobs')} />}
            {k && <Tile label="Shortlisted" value={k.shortlisted} sub={`of ${k.candidatesInFunnel} in the funnel`} onClick={() => goList('reached=shortlisted')} />}
            {k && <Tile label="Offer acceptance" value={pctText(k.offerAcceptanceRate)} sub="accepted ÷ sent" />}
            {k && <Tile label="Time to hire" value={k.avgTimeToHireDays === null ? '—' : `${k.avgTimeToHireDays} d`} sub="CV received → offer accepted" />}
            {k && <Tile label="AI interviews finished" value={pctText(k.aiCompletionRate)} sub={`${pctText(k.dropOffRate)} left before finishing`} />}
            {k && k.costPerHireUsd !== undefined && <Tile label="AI cost per hire" value={k.costPerHireUsd === null ? '—' : `$${k.costPerHireUsd}`} sub="AI spend ÷ hires" />}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, alignItems: 'start' }}>
            {/* Candidate journey as a funnel (owner, 29 Oct 2026: "have the
                candidate funnel like a funnel view"). Each step lists its candidates. */}
            <Card>
              <CardHeader title="Candidate journey" subtitle={roleId ? 'through this role\'s own rounds' : 'how far candidates are progressing'} />
              {/* The role picker on its own line (4 Nov 2026): in the header it
                  grew to the longest role's title and covered the card's title. */}
              <select className="eq-native-select" value={roleId} onChange={(e) => setRoleId(e.target.value)} aria-label="Role"
                style={{ width: '100%', maxWidth: 360, marginBottom: 16, textOverflow: 'ellipsis' }}>
                <option value="">All roles</option>
                {(Array.isArray(roles) ? roles : []).map((j) => <option key={j._id} value={j._id}>{j.title}</option>)}
              </select>
              {!roleId && <Funnel steps={data.journey} onOpen={(key) => goList(`reached=${key}`)} />}
              {roleId && !roleJourney && <span style={{ color: 'var(--text-muted)' }}>Reading the role…</span>}
              {roleId && roleJourney?.error && <span style={{ color: 'var(--status-warning-text)' }}>Could not read this role.</span>}
              {roleId && roleJourney?.steps?.length > 0 && (
                <Funnel steps={roleJourney.steps.map((s) => ({ ...s, label: s.passed !== undefined ? `${s.label} · ${s.passed} passed` : s.label }))}
                  onOpen={(key) => (key.startsWith('stage:') ? navigate(`/jobs/${roleId}`) : goList(''))} />
              )}
              {data.quickWin && (
                <div style={{ marginTop: 12, fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>
                  <Icon name="lightbulb" size={14} /> Quickest win: <span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{data.quickWin}</span>
                </div>
              )}
            </Card>

            {/* Act now: the queues, each one click away */}
            <Card>
              <CardHeader title="Act now" action={(
                <span style={{ display: 'flex', gap: 6 }}>
                  <Button size="sm" variant="ghost" icon={<Icon name="sparkle" />} onClick={() => setWin('ai')}>Ask AI</Button>
                  {data.exportAccess?.allowed && <Button size="sm" variant="ghost" icon={<Icon name="gear" />} onClick={() => setWin('actSettings')} aria-label="Act now settings" />}
                </span>
              )} />
              <div>
                {data.actNow.map((a, i) => (
                  <button key={a.key} type="button" className="eq-link-btn" onClick={() => actOn(a)}
                    style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', padding: '10px 2px', textAlign: 'left', borderBottom: i < data.actNow.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <span style={{ color: 'var(--text-1)' }}>{a.label}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 600, color: a.tone === 'amber' ? 'var(--status-warning-text)' : 'var(--text-1)' }}>{a.count}</span>
                      <Icon name="caret-right" size={14} />
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* The tools, each in its own window rather than down the page */}
          <Card>
            <CardHeader title="Messages and downloads" bare />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
              <Button variant="secondary" icon={<Icon name="megaphone" />} onClick={() => setWin('round')}>Tell candidates the next round dates</Button>
              {unpaid > 0 && <Button variant="secondary" icon={<Icon name="paper-plane-right" />} onClick={() => remindPreview('unpaid')}>Remind {unpaid} unpaid to complete registration</Button>}
              {orphans > 0 && <Button variant="secondary" icon={<Icon name="paper-plane-right" />} onClick={() => remindPreview('orphans')}>Remind {orphans} to complete the form</Button>}
              <Button variant="secondary" icon={<Icon name="list-checks" />} onClick={() => setWin('report')}>Who was reminded</Button>
              {data.exportAccess?.allowed && <Button variant="secondary" icon={<Icon name="download" />} onClick={() => setWin('export')}>Download applications</Button>}
            </div>
          </Card>
        </>
      )}

      <Modal open={win === 'round'} title="Tell candidates the next round dates" onClose={closeWin} width="860px">
        <RoundAnnouncement startOpen />
      </Modal>
      <Modal open={win === 'report'} title="Who was reminded, and who paid afterwards" onClose={closeWin} width="860px">
        <ReminderReport refreshKey={remindedRuns} />
      </Modal>
      <Modal open={win === 'nudge'} title="Invited, not taken their round yet" onClose={closeWin} width="720px">
        {win === 'nudge' && <NudgeWindow onDone={load} />}
      </Modal>
      <Modal open={win === 'setup'} title="Roles with tests or interview briefs not ready" onClose={closeWin} width="720px">
        {win === 'setup' && <SetupNotReady roles={data?.setupNotReady} onOpen={(jobId) => navigate(`/jobs/${jobId}?tab=${encodeURIComponent('Assessment Pipeline')}`)} />}
      </Modal>
      <Modal open={win === 'actSettings'} title="Act now settings" onClose={closeWin} width="640px">
        {win === 'actSettings' && data && <ActNowSettings items={data.actNowItems || data.actNow} settings={data.actNowSettings} onSaved={load} />}
      </Modal>
      <Modal open={win === 'ai'} title="What the AI suggests doing next" onClose={closeWin} width="640px">
        {win === 'ai' && <AiSuggestions />}
      </Modal>
      <Modal open={win === 'export'} title="Download applications" onClose={closeWin} width="640px">
        {data && <ApplicationsExport access={data.exportAccess} />}
      </Modal>
      <Modal open={win === 'offers'} title="Offers declined or lapsed — choose who is next" onClose={closeWin} width="760px">
        <OfferFollowUps onChanged={load} />
      </Modal>
      <Modal open={win === 'questions'} title="Questions the Application Helper could not answer" onClose={closeWin} width="760px">
        <Questions questions={questions} onAnswered={markAnswered} />
      </Modal>
      <Modal open={win === 'remind'} title={remind.target === 'orphans' ? 'Remind to complete the application form' : 'Remind unpaid applicants'} onClose={closeWin} width="760px"
        footer={remind.phase === 'confirm' && remind.count > 0 ? (
          <>
            <Button variant="ghost" onClick={closeWin}>Cancel</Button>
            <Button onClick={remindSend} icon={<Icon name="paper-plane-right" />}
              disabled={remind.target === 'unpaid' && ((remind.missing || []).length > 0 || remind.assessmentDate !== remind.previewedDate)}>Send now</Button>
          </>
        ) : null}>
        <RemindBody remind={remind} setRemind={setRemind} onDismiss={dismissOrphan} onRefresh={() => remindPreview('unpaid', remind.assessmentDate || '')} />
      </Modal>
    </div>
  );
}

/* The reminder in its window: who gets it, the words, then Send. */
function RemindBody({ remind, setRemind, onDismiss, onRefresh }) {
  const muted = { color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' };
  if (remind.phase === 'loading') return <div style={muted}><Icon name="spinner" spin /> Preparing…</div>;
  if (remind.phase === 'sending') return <div style={muted}><Icon name="spinner" spin /> Sending…</div>;
  if (remind.phase === 'error') return <div style={{ color: 'var(--status-danger-text)' }}>{remind.msg}</div>;
  if (remind.phase === 'done') return <div><StatusPill tone="success">Reminded {remind.sent}</StatusPill>{remind.failed ? <span style={{ marginLeft: 8 }}><StatusPill tone="danger">{remind.failed} failed</StatusPill></span> : null}</div>;
  if (remind.phase !== 'confirm') return null;
  if (!remind.count) return <div style={muted}>No one to remind right now — everyone was already nudged in the last 24h.</div>;
  return (
    <div style={{ display: 'grid', gap: 12, fontSize: 'var(--fs-body-sm)' }}>
      <div>
        {remind.target === 'orphans'
          ? <>Send a <strong>“complete your application”</strong> message to <strong>{remind.count}</strong> {remind.count === 1 ? 'person who' : 'people who'} paid but haven’t applied? (It tells them to fill the form — <strong>not</strong> to pay again.)</>
          : <>Send the <strong>“your application is not complete yet”</strong> reminder (email{remind.templates?.wa ? ' + WhatsApp' : ''}, with the registration link) to <strong>{remind.count}</strong> unpaid applicant{remind.count === 1 ? '' : 's'}?</>}
        {remind.skipped ? <span style={{ color: 'var(--text-muted)' }}> ({remind.skipped} skipped — reminded in the last 24h)</span> : null}
      </div>

      {remind.plan && remind.plan.length > 0 && (
        <div>
          <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>These {remind.plan.length} will be reminded:</div>
          <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
            {remind.plan.map((p, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--border)' }}>
                <span>{p.name || '(no name)'}{p.applicationId ? <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginLeft: 6 }}>{p.applicationId}</span> : null}</span>
                <span style={{ display: 'flex', gap: 10, color: 'var(--text-muted)' }}>
                  {(p.channels || []).join(' + ') || 'no channel'}
                  {remind.target === 'orphans' && p.ref && (
                    <button type="button" className="eq-link-btn" onClick={() => onDismiss(p.ref)} disabled={remind.dismissing === p.ref}
                      title="Remove from this list — a test payment, or someone who has in fact applied. They will not be messaged.">
                      {remind.dismissing === p.ref ? 'Dismissing…' : 'Dismiss'}
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
          {remind.msg && <div style={{ color: 'var(--status-danger-text)', marginTop: 4 }}>{remind.msg}</div>}
        </div>
      )}

      {remind.target === 'unpaid' && (
        <UnpaidReminderPreview remind={remind} onDate={(v) => setRemind((st) => ({ ...st, assessmentDate: v }))} onRefresh={onRefresh} />
      )}

      {/* Exactly which template, and its content — so you don't have to open Messages */}
      {remind.target !== 'unpaid' && (
        <div style={{ display: 'grid', gap: 8 }}>
          {remind.templates?.email ? (
            <Card padding="12px">
              <div style={{ color: 'var(--text-muted)' }}>Email template: <code>{remind.templates.email}</code></div>
              {remind.emailTpl ? (
                <div style={{ marginTop: 6 }}>
                  {remind.emailTpl.subject && <div style={{ fontWeight: 500, marginBottom: 4 }}>Subject: {remind.emailTpl.subject}</div>}
                  {/^\s*</.test(remind.emailTpl.body || '')
                    ? <iframe title="Email preview" sandbox="" srcDoc={(remind.emailTpl.body || '').split('cid:dps-crest').join(`${window.location.origin}/assets/dps-crest.png`)} style={{ width: '100%', height: 180, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }} />
                    : <div style={{ whiteSpace: 'pre-wrap', maxHeight: 130, overflowY: 'auto' }}>{remind.emailTpl.body}</div>}
                </div>
              ) : <div style={{ color: 'var(--status-warning-text)', marginTop: 4 }}>Content not found in Messages — check it's authored for this school.</div>}
            </Card>
          ) : <div style={{ color: 'var(--status-warning-text)' }}>No email template mapped for this gate yet (open it in Messages → Communications).</div>}
          {remind.templates?.wa ? (
            <Card padding="12px">
              <div style={{ color: 'var(--text-muted)' }}>WhatsApp template: <code>{remind.templates.wa}</code></div>
              {remind.waTpl
                ? <div style={{ whiteSpace: 'pre-wrap', maxHeight: 130, overflowY: 'auto', marginTop: 6 }}>{remind.waTpl.body}</div>
                : <div style={{ color: 'var(--status-warning-text)', marginTop: 4 }}>Content not found in Messages.</div>}
            </Card>
          ) : <div style={{ color: 'var(--text-muted)' }}>WhatsApp: not set — email only until a template is mapped.</div>}
        </div>
      )}
    </div>
  );
}

/* The unpaid reminder as the first person on the list will read it, rendered
   by the server from Messages' own templates, with the assessment date it
   states. Nothing is sent from here. */
function UnpaidReminderPreview({ remind, onDate, onRefresh }) {
  const p = remind.preview;
  const missing = remind.missing || [];
  const warn = { color: 'var(--status-warning-text)' };
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <label style={{ color: 'var(--text-muted)' }}>
        Online assessment date (said in the message)
        <span style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <input value={remind.assessmentDate || ''} onChange={(e) => onDate(e.target.value)} placeholder="e.g. Saturday, 28th November 2026"
            className="eq-native-select" style={{ flex: 1 }} />
          <Button size="sm" variant="secondary" onClick={onRefresh}>Update preview</Button>
        </span>
      </label>
      {remind.assessmentDate !== remind.previewedDate && <div style={warn}>Update the preview to see the message with this date before sending.</div>}
      {missing.length > 0 && <div style={warn}>Type the {missing.join(' and ').toLowerCase()} and update the preview to see the message. Nothing can be sent without it.</div>}
      {p && (
        <>
          <div style={{ color: 'var(--text-muted)' }}>As {p.forName} will read it:</div>
          {p.email && (
            <Card padding="12px">
              <div style={{ color: 'var(--text-muted)' }}>Email</div>
              <div style={{ fontWeight: 500, marginTop: 4 }}>Subject: {p.email.subject}</div>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{p.email.text}</div>
            </Card>
          )}
          {p.whatsapp && (
            <Card padding="12px">
              <div style={{ color: 'var(--text-muted)' }}>WhatsApp{p.whatsapp.approved ? '' : ' — the current approved message, until Meta approves the new wording'}</div>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{p.whatsapp.text || '(its text could not be read from Messages)'}</div>
            </Card>
          )}
        </>
      )}
      {!p && !missing.length && <div style={warn}>The message could not be read from Messages, so it is not shown here.</div>}
    </div>
  );
}

function Questions({ questions, onAnswered }) {
  const muted = { color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' };
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={muted}>Reply to the candidate yourself, then mark the question answered.</div>
      {questions.loading && <div style={muted}><Icon name="spinner" spin /> Loading…</div>}
      {!questions.loading && questions.items.length === 0 && <div style={muted}>No questions waiting.</div>}
      {questions.items.map((q) => (
        <div key={q.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
          <div style={{ minWidth: 0 }}>
            <div>&ldquo;{q.question}&rdquo;</div>
            <div style={{ ...muted, marginTop: 2 }}>
              {q.candidateName || 'Candidate'}
              {q.roles?.length ? ` · ${q.roles.join(', ')}` : ''}
              {q.applicationIds?.length ? ` · ${q.applicationIds.join(', ')}` : ''}
              {q.askedAt ? ` · asked ${new Date(q.askedAt).toLocaleString()}` : ''}
              {` · passed on by ${q.raisedBy}`}
            </div>
          </div>
          <Button size="sm" variant="secondary" icon={<Icon name="check-circle" />} onClick={() => onAnswered(q.id)}>Mark answered</Button>
        </div>
      ))}
      {questions.msg && <div style={{ color: 'var(--status-danger-text)' }}>{questions.msg}</div>}
    </div>
  );
}

function Funnel({ steps, onOpen }) {
  const last = Math.max(1, steps.length - 1);
  return (
    <div style={{ display: 'grid', gap: 4, justifyItems: 'center', padding: '4px 0' }}>
      {steps.map((s, i) => {
        const width = 100 - ((100 - FUNNEL_NARROWEST) * i) / last;
        const shade = FUNNEL_SHADES[Math.min(i, FUNNEL_SHADES.length - 1)];
        return (
          <button key={s.key} type="button" onClick={() => onOpen(s.key)} aria-label={`${s.label}: ${s.count}`}
            style={{
              width: `${width}%`, minWidth: 200, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              padding: '11px 20px', border: 0, cursor: 'pointer', font: 'inherit', fontSize: 'var(--fs-body-md)',
              background: shade, color: 'var(--text-on-dark)',
              clipPath: 'polygon(0 0, 100% 0, calc(100% - 10px) 100%, 10px 100%)',
            }}>
            <span style={{ fontWeight: 500, paddingLeft: 6 }}>{s.label}</span>
            <span className="num" style={{ fontWeight: 700, paddingRight: 6 }}>{s.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function Tile({ label, value, sub, onClick }) {
  const body = (
    <>
      <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 'var(--fs-display-md)', fontWeight: 600, lineHeight: 1.2, margin: '6px 0 2px' }}>{value}</div>
      {sub && <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>{sub}</div>}
    </>
  );
  return onClick
    ? <Card padding="16px" role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }} style={{ cursor: 'pointer' }}>{body}</Card>
    : <Card padding="16px">{body}</Card>;
}
